import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Baby,
  Bandage,
  CalendarCheck,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  HeartPulse,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Send,
  Syringe,
  Stethoscope,
  UserRound,
  Venus,
  X,
} from "lucide-react";
import { branches, doctors, faqs, primaryDomain, promotions, services, type Branch, type Service } from "./data/site";
import { buildBookingMessage, buildWhatsAppLink, validateBookingRequest, type BookingInput } from "./lib/booking";
import "./styles.css";

type Route =
  | "home"
  | "services"
  | "service"
  | "doctors"
  | "branches"
  | "about"
  | "promotions"
  | "faq"
  | "booking"
  | "privacy";

const navItems = [
  { label: "Perkhidmatan", path: "/services", dropdown: true },
  { label: "Cawangan", path: "/branches" },
  { label: "Pemeriksaan Kesihatan", path: "/services/saringan-kesihatan" },
  { label: "Promosi", path: "/promotions" },
  { label: "Tentang Kami", path: "/about" },
  { label: "Artikel Kesihatan", path: "/faq" },
];

function getPath() {
  return window.location.pathname.replace(/\/$/, "") || "/";
}

function routeFromPath(path: string): { route: Route; service?: Service } {
  if (path.startsWith("/services/")) {
    return { route: "service", service: services.find((item) => item.id === path.split("/").pop()) ?? services[0] };
  }
  if (path === "/services") return { route: "services" };
  if (path === "/doctors") return { route: "doctors" };
  if (path === "/branches" || path === "/contact") return { route: "branches" };
  if (path === "/about") return { route: "about" };
  if (path === "/promotions") return { route: "promotions" };
  if (path === "/faq") return { route: "faq" };
  if (path === "/booking") return { route: "booking" };
  if (path === "/privacy") return { route: "privacy" };
  return { route: "home" };
}

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "auto" });
}

function useRoute() {
  const [path, setPath] = useState(getPath());

  useEffect(() => {
    const onPop = () => setPath(getPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return routeFromPath(path);
}

function useSelectedBranch() {
  const [branchId, setBranchId] = useState(() => localStorage.getItem("usrah-branch") ?? branches[0].id);
  const branch = branches.find((item) => item.id === branchId) ?? branches[0];

  const update = (id: string) => {
    localStorage.setItem("usrah-branch", id);
    setBranchId(id);
  };

  return { branch, branchId, setBranchId: update };
}

function setPageMeta(title: string, description: string, path: string) {
  document.title = `${title} | Usrah Medic`;
  const ensure = (selector: string, create: () => HTMLMetaElement | HTMLLinkElement) => {
    const found = document.querySelector(selector);
    if (found) return found;
    const node = create();
    document.head.appendChild(node);
    return node;
  };
  ensure("meta[name='description']", () => {
    const meta = document.createElement("meta");
    meta.name = "description";
    return meta;
  }).setAttribute("content", description);
  ensure("link[rel='canonical']", () => {
    const link = document.createElement("link");
    link.rel = "canonical";
    return link;
  }).setAttribute("href", `${primaryDomain}${path}`);
}

function CtaLink({
  branch,
  service,
  children,
  className = "button whatsapp",
  ...props
}: { branch: Branch; service?: Service; children: React.ReactNode; className?: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      className={className}
      href={buildWhatsAppLink({
        branch,
        message: buildBookingMessage({ branch, service }),
      })}
      target="_blank"
      rel="noreferrer"
      {...props}
    >
      {children}
    </a>
  );
}

function Header({ branch, setBranchId }: { branch: Branch; setBranchId: (id: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="site-header">
      <a className="brand" href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>
        <span className="brand-mark" aria-hidden="true">
          <HeartPulse size={26} />
        </span>
        <span className="brand-text">
          <strong>USRAH</strong>
          <small>MEDIC</small>
        </span>
      </a>
      <nav className="desktop-nav" aria-label="Navigasi utama">
        {navItems.map((item) => (
          <a key={item.path} href={item.path} onClick={(event) => { event.preventDefault(); navigate(item.path); }}>
            {item.label}
            {item.dropdown ? <ChevronDown size={14} /> : null}
          </a>
        ))}
      </nav>
      <div className="header-actions">
        <label className="branch-select">
          <span className="sr-only">Pilih cawangan</span>
          <MapPin size={18} aria-hidden="true" />
          <select value={branch.id} onChange={(event) => setBranchId(event.target.value)}>
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {item.shortName}
              </option>
            ))}
          </select>
          <span className="branch-select-display">Pilih Cawangan</span>
          <ChevronDown size={16} aria-hidden="true" />
        </label>
        <button className="button primary header-booking" onClick={() => navigate("/booking")}>
          <CalendarCheck size={18} /> Tempah Slot
        </button>
        <CtaLink branch={branch} className="icon-button whatsapp" aria-label="WhatsApp klinik">
          <MessageCircle size={20} />
        </CtaLink>
        <button className="icon-button menu-button" onClick={() => setOpen(true)} aria-label="Buka menu">
          <Menu size={22} />
        </button>
      </div>
      {open ? (
        <div className="mobile-panel" role="dialog" aria-modal="true" aria-label="Menu mudah alih">
          <button className="icon-button close-button" onClick={() => setOpen(false)} aria-label="Tutup menu">
            <X size={22} />
          </button>
          {navItems.map((item) => (
            <a key={item.path} href={item.path} onClick={(event) => { event.preventDefault(); setOpen(false); navigate(item.path); }}>
              {item.label}
            </a>
          ))}
          <button className="button primary" onClick={() => { setOpen(false); navigate("/booking"); }}>Tempah Slot</button>
        </div>
      ) : null}
    </header>
  );
}

function Hero({ branch }: { branch: Branch }) {
  const trustItems = [
    { label: "Doktor Wanita", icon: <UserRound size={22} /> },
    { label: "Mesra Keluarga", icon: <HeartPulse size={22} /> },
    { label: "Rawatan Menyeluruh", icon: <ClipboardCheck size={22} /> },
    { label: "Ihsan & Empati", icon: <ShieldCheck size={22} /> },
  ];

  return (
    <section className="hero">
      <div className="hero-copy">
        <h1>
          <span>Klinik keluarga</span>
          <span><strong>24 jam</strong> yang</span>
          <span>mudah dihubungi</span>
        </h1>
        <p>
          Perkhidmatan perubatan menyeluruh untuk seisi keluarga. Doktor wanita, persekitaran selesa, rawatan dengan ihsan.
        </p>
        <div className="cta-row">
          <button className="button primary" onClick={() => navigate("/booking")}>
            <CalendarCheck size={18} /> Tempah Slot
          </button>
          <CtaLink branch={branch}>
            <MessageCircle size={18} /> WhatsApp Klinik
          </CtaLink>
        </div>
        <div className="hero-status-card">
          <span className="status-icon">24</span>
          <span>
            <strong>Klinik Ibu Pejabat Puncak Alam</strong>
            Beroperasi <b>24 jam</b> setiap hari
          </span>
        </div>
      </div>
      <div className="hero-media-wrap">
        <div className="hero-visual" aria-label="Suasana konsultasi keluarga di klinik">
          <img src="/images/clinic-family-consultation.png" alt="Doktor wanita berbincang dengan ibu dan anak di klinik keluarga" />
        </div>
        <div className="hero-wall-logo" aria-hidden="true">
          <HeartPulse size={40} />
          <span>
            <strong>USRAH</strong>
            <small>MEDIC</small>
          </span>
        </div>
        <div className="hero-trust-list">
          {trustItems.map((item) => (
            <span key={item.label}>
              {item.icon}
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

const serviceHighlights = [
  { label: "Rawatan & Kecemasan 24 jam", icon: <Stethoscope size={34} />, emphasis: "24 jam" },
  { label: "Kecederaan Kecil & Jahitan", icon: <Bandage size={34} /> },
  { label: "Ultrasound (Scan)", icon: <HeartPulse size={34} /> },
  { label: "Antenatal & Postnatal", icon: <UserRound size={34} /> },
  { label: "Pediatrik & Susulan Bayi", icon: <Baby size={34} /> },
  { label: "Penyakit Kronik & Gaya Hidup", icon: <HeartPulse size={34} /> },
  { label: "Pemeriksaan Kesihatan", icon: <ClipboardCheck size={34} /> },
  { label: "Pap Smear & Kesihatan Wanita", icon: <Venus size={34} /> },
  { label: "Nebuliser", icon: <Stethoscope size={34} /> },
  { label: "Pemantauan Jaundice", icon: <Syringe size={34} /> },
];

function ServiceIconRow() {
  return (
    <section className="service-ribbon" aria-label="Perkhidmatan kami">
      <div className="service-ribbon-intro">
        <h2>Perkhidmatan Kami</h2>
        <p>Satu klinik, pelbagai keperluan kesihatan anda.</p>
      </div>
      <div className="service-icon-row">
        {serviceHighlights.map((item) => (
          <button
            className="service-icon-item"
            key={item.label}
            onClick={() => navigate(item.label.includes("Ultrasound") ? "/services/ultrasound" : "/services")}
          >
            {item.icon}
            <span>{item.label}</span>
            {item.emphasis ? <b>{item.emphasis}</b> : null}
          </button>
        ))}
      </div>
    </section>
  );
}

function BranchCards({ branch, setBranchId }: { branch: Branch; setBranchId: (id: string) => void }) {
  return (
    <section className="section compact">
      <div className="section-heading">
        <h2>Pilih cawangan berhampiran anda</h2>
        <p>Setiap CTA WhatsApp akan ikut cawangan pilihan supaya mesej pesakit terus sampai ke pasukan yang betul.</p>
      </div>
      <div className="branch-grid">
        {branches.map((item) => (
          <article className={`branch-card ${branch.id === item.id ? "selected" : ""}`} key={item.id}>
            <h3>{item.shortName}</h3>
            <p>{item.hours}</p>
            <p>{item.address}</p>
            <div className="card-actions">
              <button className="button secondary" onClick={() => setBranchId(item.id)}>Pilih</button>
              <CtaLink branch={item} className="button whatsapp small"><MessageCircle size={16} /> WhatsApp</CtaLink>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ServicesGrid({ branch }: { branch: Branch }) {
  return (
    <section className="section">
      <div className="section-heading">
        <h2>Servis utama</h2>
        <p>Disusun untuk pesakit memilih tindakan dengan cepat: walk-in, tempah slot atau WhatsApp cawangan.</p>
      </div>
      <div className="service-grid">
        {services.map((service) => (
          <article className="service-card" key={service.id}>
            <div className="service-icon">{service.category.includes("Wanita") ? <HeartPulse /> : service.category.includes("Kanak") ? <Baby /> : <Stethoscope />}</div>
            <span>{service.category}</span>
            <h3>{service.title}</h3>
            <p>{service.summary}</p>
            <div className="card-actions">
              <a href={`/services/${service.id}`} onClick={(event) => { event.preventDefault(); navigate(`/services/${service.id}`); }}>
                Lihat detail
              </a>
              <CtaLink branch={branch} service={service} className="text-whatsapp">WhatsApp</CtaLink>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function BookingPanel({ branch, selectedService, compact = false }: { branch: Branch; selectedService?: Service; compact?: boolean }) {
  const [form, setForm] = useState<BookingInput>({
    branchId: branch.id,
    serviceId: selectedService?.id ?? "ultrasound",
    name: "",
    phone: "",
    email: "",
    preferredDate: "",
    preferredWindow: "",
    notes: "",
    consent: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingInput, string>>>({});
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setForm((current) => ({ ...current, branchId: branch.id }));
  }, [branch.id]);

  const activeBranch = branches.find((item) => item.id === form.branchId) ?? branch;
  const activeService = services.find((item) => item.id === form.serviceId);
  const whatsappUrl = buildWhatsAppLink({
    branch: activeBranch,
    message: buildBookingMessage({
      branch: activeBranch,
      service: activeService,
      name: form.name,
      phone: form.phone,
      email: form.email,
      preferredDate: form.preferredDate,
      preferredWindow: form.preferredWindow,
      notes: form.notes,
      consent: form.consent,
    }),
  });

  const update = (key: keyof BookingInput, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = validateBookingRequest(form);
    if (!result.ok) {
      setErrors(result.errors);
      setSuccess(null);
      return;
    }
    setErrors({});
    try {
      const response = await fetch("/api/booking", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(result.data),
      });
      setSuccess(response.ok ? "Permintaan diterima. Klinik akan sahkan slot melalui panggilan atau WhatsApp." : "Permintaan disediakan. Sila teruskan melalui WhatsApp.");
    } catch {
      setSuccess("Permintaan disediakan. Sila teruskan melalui WhatsApp.");
    }
  };

  return (
    <form className="booking-form" onSubmit={submit}>
      <div className="booking-form-header">
        <div>
          <h2>Permintaan Temujanji</h2>
          <p>Isi maklumat ringkas, kami akan sahkan melalui WhatsApp.</p>
        </div>
        <span><ShieldCheck size={18} /> Maklumat anda selamat dengan kami.</span>
      </div>
      <div className="form-grid">
        <label className="hide-on-home-form">
          Cawangan
          <select value={form.branchId} onChange={(event) => update("branchId", event.target.value)}>
            {branches.map((item) => <option key={item.id} value={item.id}>{item.shortName}</option>)}
          </select>
          {errors.branchId ? <small>{errors.branchId}</small> : null}
        </label>
        <label>
          Nama penuh
          <input value={form.name} onChange={(event) => update("name", event.target.value)} autoComplete="name" placeholder="Contoh: Aisyah Binti Ahmad" />
          {errors.name ? <small>{errors.name}</small> : null}
        </label>
        <label>
          Nombor telefon
          <input value={form.phone} onChange={(event) => update("phone", event.target.value)} autoComplete="tel" inputMode="tel" placeholder="Contoh: 012-345 6789" />
          {errors.phone ? <small>{errors.phone}</small> : null}
        </label>
        <label>
          E-mel (pilihan)
          <input value={form.email} onChange={(event) => update("email", event.target.value)} autoComplete="email" inputMode="email" placeholder="contoh: nama@email.com" />
        </label>
        <label>
          Tarikh temujanji
          <input type="date" value={form.preferredDate} onChange={(event) => update("preferredDate", event.target.value)} />
          {errors.preferredDate ? <small>{errors.preferredDate}</small> : null}
        </label>
        <label>
          Masa pilihan
          <select value={form.preferredWindow} onChange={(event) => update("preferredWindow", event.target.value)}>
            <option value="">Pilih masa</option>
            <option>Pagi</option>
            <option>Tengah hari</option>
            <option>Petang</option>
            <option>Malam</option>
          </select>
          {errors.preferredWindow ? <small>{errors.preferredWindow}</small> : null}
        </label>
        <label>
          Perkhidmatan
          <select value={form.serviceId} onChange={(event) => update("serviceId", event.target.value)}>
            {services.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
          </select>
          {errors.serviceId ? <small>{errors.serviceId}</small> : null}
        </label>
      </div>
      <label>
        Catatan (pilihan)
        <textarea value={form.notes} onChange={(event) => update("notes", event.target.value)} rows={compact ? 2 : 3} placeholder="Nyatakan simptom atau keperluan khusus (cth: susulan bayi, luka, demam)" />
      </label>
      <label className="consent-row">
        <input type="checkbox" checked={Boolean(form.consent)} onChange={(event) => update("consent", event.target.checked)} />
        <span>Saya bersetuju dengan <a href="/privacy" onClick={(event) => { event.preventDefault(); navigate("/privacy"); }}>Dasar Privasi</a>.</span>
      </label>
      {errors.consent ? <small className="field-error">{errors.consent}</small> : null}
      <div className="form-actions">
        {!compact ? (
          <a className="button whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer">
            <MessageCircle size={18} /> Teruskan di WhatsApp
          </a>
        ) : null}
        <button className="button primary" type="submit">
          Hantar Permintaan <Send size={16} />
        </button>
      </div>
      {success ? <p className="success-state" role="status">{success}</p> : null}
    </form>
  );
}

function HomeAppointmentSection({ branch, setBranchId }: { branch: Branch; setBranchId: (id: string) => void }) {
  return (
    <section className="appointment-band">
      <div className="branch-selector-panel">
        <h2>Pilih Cawangan</h2>
        <div className="home-branch-list">
          {branches.map((item) => (
            <button className={branch.id === item.id ? "active" : ""} key={item.id} onClick={() => setBranchId(item.id)}>
              <MapPin size={19} />
              <span>{item.shortName}</span>
              <small>{item.hours.replace("Setiap hari, ", "")}</small>
            </button>
          ))}
        </div>
        <button className="branch-more" onClick={() => navigate("/branches")}>Lihat semua cawangan →</button>
      </div>
      <BookingPanel branch={branch} compact />
    </section>
  );
}

function SupportStrip({ branch }: { branch: Branch }) {
  return (
    <section className="support-strip">
      <div>
        <Phone size={25} />
        <strong>Perlukan bantuan segera?</strong>
        <span>WhatsApp kami untuk respons pantas.</span>
      </div>
      <CtaLink branch={branch}><MessageCircle size={18} /> WhatsApp Klinik</CtaLink>
    </section>
  );
}

function HomePage({ branch, setBranchId }: { branch: Branch; setBranchId: (id: string) => void }) {
  useEffect(() => setPageMeta("Klinik keluarga 24 jam", "Usrah Medic ialah klinik keluarga Malay-first dengan WhatsApp cawangan dan permintaan slot temujanji.", "/"), []);

  return (
    <>
      <Hero branch={branch} />
      <ServiceIconRow />
      <HomeAppointmentSection branch={branch} setBranchId={setBranchId} />
      <SupportStrip branch={branch} />
    </>
  );
}

function ServicesPage({ branch }: { branch: Branch }) {
  useEffect(() => setPageMeta("Servis klinik", "Senarai servis Usrah Medic termasuk ultrasound, rawatan keluarga, paediatric, saringan dan kecemasan ringan.", "/services"), []);
  return <><PageHero title="Servis klinik yang mudah difahami" body="Pilih servis, semak persediaan dan terus WhatsApp cawangan dengan konteks yang betul." /><ServicesGrid branch={branch} /></>;
}

function ServiceDetailPage({ branch, service }: { branch: Branch; service: Service }) {
  useEffect(() => setPageMeta(service.title, service.summary, `/services/${service.id}`), [service]);
  return (
    <>
      <PageHero title={service.title} body={service.detail} />
      <section className="section split">
        <article className="detail-panel">
          <h2>Apa yang perlu tahu</h2>
          <p><strong>Ketersediaan:</strong> {service.availability}</p>
          <h3>Persediaan sebelum hadir</h3>
          <ul>{service.preparation.map((item) => <li key={item}>{item}</li>)}</ul>
          <h3>Cawangan berkaitan</h3>
          <div className="chip-row">{service.branches.map((id) => <span key={id}>{branches.find((item) => item.id === id)?.shortName}</span>)}</div>
        </article>
        <aside className="cta-panel">
          <h2>Semak slot {service.title}</h2>
          <p>Mesej WhatsApp akan membawa nama servis dan cawangan pilihan.</p>
          <div className="cta-row vertical">
            <button className="button primary" onClick={() => navigate("/booking")}>Tempah Slot</button>
            <CtaLink branch={branch} service={service}><MessageCircle size={18} /> WhatsApp Cawangan</CtaLink>
          </div>
        </aside>
      </section>
    </>
  );
}

function DoctorsPage({ branch }: { branch: Branch }) {
  useEffect(() => setPageMeta("Profil doktor", "Profil doktor Usrah Medic mengikut cawangan dan bidang fokus klinikal.", "/doctors"), []);
  return (
    <>
      <PageHero title="Doktor dan pasukan klinikal" body="Semak doktor mengikut cawangan. Jadual doktor wanita tertakluk kepada operasi cawangan dan perlu disahkan." />
      <section className="section doctor-grid">
        {doctors.map((doctor) => {
          const doctorBranch = branches.find((item) => item.id === doctor.branchId) ?? branch;
          return (
            <article className="doctor-card" key={doctor.id}>
              <div className="avatar"><UserRound /></div>
              <span>{doctorBranch.shortName}</span>
              <h2>{doctor.name}</h2>
              <p>{doctor.role} • {doctor.credential}</p>
              <div className="chip-row">{doctor.focus.map((item) => <span key={item}>{item}</span>)}</div>
              <CtaLink branch={doctorBranch} className="button secondary">Request appointment</CtaLink>
            </article>
          );
        })}
      </section>
    </>
  );
}

function BranchesPage({ branch, setBranchId }: { branch: Branch; setBranchId: (id: string) => void }) {
  useEffect(() => setPageMeta("Cawangan dan kontak", "Alamat, waktu operasi, telefon, WhatsApp dan Waze setiap cawangan Usrah Medic.", "/branches"), []);
  return (
    <>
      <PageHero title="Cawangan, waktu operasi dan WhatsApp" body="Pilih cawangan untuk terus hubungi klinik, buka Waze atau semak slot temujanji." />
      <BranchCards branch={branch} setBranchId={setBranchId} />
      <section className="section branch-list">
        {branches.map((item) => (
          <article className="detail-panel" key={item.id}>
            <h2>{item.name}</h2>
            <p><Clock3 size={16} /> {item.hours}</p>
            <p><Phone size={16} /> {item.phone}</p>
            <p><MapPin size={16} /> {item.address}</p>
            <div className="chip-row">{item.services.map((service) => <span key={service}>{service}</span>)}</div>
            <div className="card-actions">
              <CtaLink branch={item} className="button whatsapp"><MessageCircle size={18} /> WhatsApp</CtaLink>
              <a className="button secondary" href={item.wazeUrl} target="_blank" rel="noreferrer">Waze</a>
              <a className="button secondary" href={item.mapsUrl} target="_blank" rel="noreferrer">Maps</a>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}

function AboutPage({ branch }: { branch: Branch }) {
  useEffect(() => setPageMeta("Tentang Usrah Medic", "Usrah Medic memberi fokus kepada penjagaan keluarga, wanita dan kanak-kanak dengan rangkaian klinik mudah dihubungi.", "/about"), []);
  return (
    <>
      <PageHero title="Klinik keluarga yang dekat dengan komuniti" body="Laman baharu ini mengekalkan kekuatan Usrah Medic: akses cawangan, doktor wanita mengikut jadual, rawatan harian dan komunikasi cepat melalui WhatsApp." />
      <section className="section trust-grid">
        {["Klinik keluarga", "Wanita & kanak-kanak", "Cawangan jelas", "Sokongan WhatsApp"].map((item) => <article className="trust-card" key={item}><ShieldCheck /><h2>{item}</h2><p>Disusun supaya pesakit faham pilihan rawatan dan tindakan seterusnya tanpa perlu mencari terlalu lama.</p></article>)}
      </section>
      <section className="section cta-band"><h2>Perlu semak servis atau slot?</h2><CtaLink branch={branch}><MessageCircle size={18} /> WhatsApp Klinik</CtaLink></section>
    </>
  );
}

function PromotionsPage({ branch }: { branch: Branch }) {
  useEffect(() => setPageMeta("Promosi", "Promosi dan kempen Usrah Medic yang boleh disemak melalui WhatsApp cawangan.", "/promotions"), []);
  return (
    <>
      <PageHero title="Promosi dan kempen semasa" body="Promosi dipersembahkan sebagai tindakan jelas: semak syarat, pilih cawangan dan hubungi klinik." />
      <section className="section promo-grid">
        {promotions.map((promo) => {
          const service = services.find((item) => item.id === promo.serviceId);
          return <article className="service-card" key={promo.title}><h2>{promo.title}</h2><p>{promo.summary}</p><CtaLink branch={branch} service={service} className="button whatsapp">Semak melalui WhatsApp</CtaLink></article>;
        })}
      </section>
    </>
  );
}

function FaqPage() {
  useEffect(() => setPageMeta("FAQ", "Soalan lazim tentang waktu operasi, walk-in, booking dan WhatsApp Usrah Medic.", "/faq"), []);
  return <><PageHero title="Soalan lazim" body="Jawapan ringkas untuk pesakit sebelum walk-in, WhatsApp atau request slot." /><FaqSection /></>;
}

function BookingPage({ branch }: { branch: Branch }) {
  useEffect(() => setPageMeta("Tempah slot", "Hantar permintaan slot temujanji Usrah Medic dan teruskan melalui WhatsApp jika perlu.", "/booking"), []);
  return <section className="section booking-page"><PageHero title="Request appointment slot" body="Pilih cawangan, servis, tarikh dan masa pilihan. Kami bingkaikan sebagai request slot kerana live inventory belum disambungkan." /><BookingPanel branch={branch} /></section>;
}

function PrivacyPage() {
  useEffect(() => setPageMeta("Dasar privasi", "Ringkasan privasi untuk permintaan temujanji Usrah Medic.", "/privacy"), []);
  return <PageHero title="Dasar privasi" body="Maklumat dalam borang digunakan untuk menghubungi pesakit berkenaan permintaan slot. Jangan hantar maklumat kecemasan atau data sensitif yang tidak diperlukan melalui borang umum." />;
}

function PageHero({ title, body }: { title: string; body: string }) {
  return <section className="page-hero"><h1>{title}</h1><p>{body}</p></section>;
}

function FaqSection() {
  return <section className="section faq-list">{faqs.map((faq) => <details key={faq.question}><summary>{faq.question}</summary><p>{faq.answer}</p></details>)}</section>;
}

function Footer({ branch }: { branch: Branch }) {
  return (
    <footer className="site-footer">
      <div>
        <h2>Usrah Medic</h2>
        <p>Klinik keluarga Malay-first untuk rawatan harian, wanita, kanak-kanak dan pemeriksaan kesihatan.</p>
      </div>
      <div>
        <h3>Cawangan pilihan</h3>
        <p>{branch.shortName}</p>
        <p>{branch.hours}</p>
      </div>
      <div>
        <h3>Menu</h3>
        <a href="/services" onClick={(event) => { event.preventDefault(); navigate("/services"); }}>Servis</a>
        <a href="/branches" onClick={(event) => { event.preventDefault(); navigate("/branches"); }}>Cawangan</a>
        <a href="/privacy" onClick={(event) => { event.preventDefault(); navigate("/privacy"); }}>Privasi</a>
      </div>
      <p className="footer-note">Maklumat laman ini bersifat umum dan bukan pengganti konsultasi doktor. Untuk kecemasan serius, hubungi 999 atau ke Jabatan Kecemasan.</p>
    </footer>
  );
}

function StickyActions({ branch }: { branch: Branch }) {
  return (
    <div className="sticky-actions">
      <CtaLink branch={branch} className="button whatsapp"><MessageCircle size={18} /> WhatsApp</CtaLink>
      <button className="button primary" onClick={() => navigate("/booking")}><CalendarCheck size={18} /> Tempah</button>
    </div>
  );
}

function Schema() {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Usrah Medic",
    url: primaryDomain,
    medicalSpecialty: ["PrimaryCare", "Obstetric", "Pediatric"],
    telephone: branches[0].phone,
    address: branches[0].address,
    branchOf: branches.map((item) => ({
      "@type": "MedicalClinic",
      name: item.name,
      telephone: item.phone,
      address: item.address,
      openingHours: item.hours,
    })),
  }), []);

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

function App() {
  const { route, service } = useRoute();
  const { branch, setBranchId } = useSelectedBranch();

  return (
    <>
      <Schema />
      <Header branch={branch} setBranchId={setBranchId} />
      <main>
        {route === "home" ? <HomePage branch={branch} setBranchId={setBranchId} /> : null}
        {route === "services" ? <ServicesPage branch={branch} /> : null}
        {route === "service" && service ? <ServiceDetailPage branch={branch} service={service} /> : null}
        {route === "doctors" ? <DoctorsPage branch={branch} /> : null}
        {route === "branches" ? <BranchesPage branch={branch} setBranchId={setBranchId} /> : null}
        {route === "about" ? <AboutPage branch={branch} /> : null}
        {route === "promotions" ? <PromotionsPage branch={branch} /> : null}
        {route === "faq" ? <FaqPage /> : null}
        {route === "booking" ? <BookingPage branch={branch} /> : null}
        {route === "privacy" ? <PrivacyPage /> : null}
      </main>
      <Footer branch={branch} />
      <StickyActions branch={branch} />
    </>
  );
}

createRoot(document.getElementById("app")!).render(<App />);
